import { Controller } from '@hotwired/stimulus';
import {
  generateRecoveryCode,
  wrapPrivateKeyWithPassword,
  loadOrgKeyFromStorage,
  b64encode,
} from '../vault_crypto';
import { saveText } from '../save_blob';

export default class extends Controller {
  static targets = [
    'currentSecret',
    'newRecoveryCode',
    'newRecoveryCodeVault',
    'orgWrappedKey',
    'orgSalt',
    'orgIv',
    'submitButton',
    'submitVaultButton',
    'rotateCard',
    'vaultCard',
    'backLink',
    'copyLabel',
  ];
  static values = { cryptoOptions: Object, t: Object };

  initialize() {
    this._downloaded = false;
    this._downloadedVault = false;
  }

  async generate(event) {
    event.preventDefault();

    const currentSecret = this.currentSecretTarget.value;
    if (!currentSecret) {
      alert(this.tValue.current_secret_required);
      return;
    }

    try {
      const opts = this.cryptoOptionsValue;
      const decryptor = new Decrypt({
        wrapped_key: opts.wrapped_key,
        salt: opts.salt,
        iv: opts.iv,
        enc_shared_keys: [],
      });
      await decryptor.setPasswordUnwrapKey(currentSecret, { suppressAlert: true });
    } catch (e) {
      alert(this.tValue.invalid_secret);
      return;
    }

    const code = generateRecoveryCode();
    this.newRecoveryCodeTarget.value = code;
    saveText(code, 'recovery-code.txt');
    this._downloaded = true;
    if (this.hasBackLinkTarget) this.backLinkTarget.classList.add('hidden');

    if (this.hasRotateCardTarget) {
      const multistep = this.application.getControllerForElementAndIdentifier(
        this.rotateCardTarget,
        'multistep'
      );
      multistep?.goToStep(1);
    }
  }

  generateVault(event) {
    event.preventDefault();
    const code = generateRecoveryCode();
    this.newRecoveryCodeVaultTarget.value = code;
    saveText(code, 'recovery-code.txt');
    this._downloadedVault = true;
    if (this.hasBackLinkTarget) this.backLinkTarget.classList.add('hidden');

    if (this.hasVaultCardTarget) {
      const multistep = this.application.getControllerForElementAndIdentifier(
        this.vaultCardTarget,
        'multistep'
      );
      multistep?.goToStep(1);
    }
  }

  async copyNewCode(event) {
    event?.preventDefault();
    const field =
      this.hasNewRecoveryCodeTarget && this.newRecoveryCodeTarget.value
        ? this.newRecoveryCodeTarget
        : this.hasNewRecoveryCodeVaultTarget
          ? this.newRecoveryCodeVaultTarget
          : null;
    const code = field?.value;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return; // clipboard blocked (e.g. insecure context); leave the field for manual copy
    }

    if (this.hasCopyLabelTarget) {
      this.copyLabelTarget.textContent = this.tValue.copied;
      clearTimeout(this._copyResetTimer);
      this._copyResetTimer = setTimeout(() => {
        if (this.hasCopyLabelTarget) this.copyLabelTarget.textContent = this.tValue.copy;
      }, 1500);
    }
  }

  async submit(event) {
    event.preventDefault();

    const currentSecret = this.currentSecretTarget.value;
    const newRecoveryCode = this.newRecoveryCodeTarget.value;

    if (!currentSecret) {
      alert(this.tValue.current_secret_required);
      return;
    }
    if (!newRecoveryCode || !this._downloaded) {
      alert(this.tValue.recovery_code_required);
      return;
    }

    try {
      const opts = this.cryptoOptionsValue;
      const decryptor = new Decrypt({
        wrapped_key: opts.wrapped_key,
        salt: opts.salt,
        iv: opts.iv,
        enc_shared_keys: [],
      });
      await decryptor.setPasswordUnwrapKey(currentSecret, { suppressAlert: true });

      const { wrappedKey, salt, iv } = await wrapPrivateKeyWithPassword(
        newRecoveryCode,
        decryptor.privateKey
      );

      this.orgWrappedKeyTarget.value = b64encode(wrappedKey);
      this.orgSaltTarget.value = b64encode(salt);
      this.orgIvTarget.value = b64encode(iv);

      this.currentSecretTarget.disabled = true;
      this.newRecoveryCodeTarget.disabled = true;
      this.element.submit();
    } catch (e) {
      console.error('Recovery code rotation failed:', e);
      alert(this.tValue.invalid_secret);
    }
  }

  submitFromVault(event) {
    event.preventDefault();
    const newRecoveryCode = this.newRecoveryCodeVaultTarget.value;
    if (!newRecoveryCode || !this._downloadedVault) {
      alert(this.tValue.recovery_code_required);
      return;
    }

    if (window.vault?.is_open()) {
      this._doRotateFromVault(newRecoveryCode);
    } else {
      window.vault?.openModal(() => this._doRotateFromVault(newRecoveryCode));
    }
  }

  async _doRotateFromVault(newRecoveryCode) {
    try {
      const orgPrivKey = await loadOrgKeyFromStorage();
      const { wrappedKey, salt, iv } = await wrapPrivateKeyWithPassword(
        newRecoveryCode,
        orgPrivKey
      );

      this.orgWrappedKeyTarget.value = b64encode(wrappedKey);
      this.orgSaltTarget.value = b64encode(salt);
      this.orgIvTarget.value = b64encode(iv);

      this.newRecoveryCodeVaultTarget.disabled = true;
      this.element.submit();
    } catch (e) {
      console.error('Recovery code rotation from vault failed:', e);
      alert(this.tValue.rotation_failed);
    }
  }
}
